import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, Shield } from 'lucide-react';
import { setFileAccess } from '../../../services/apiClient';

/**
 * Human-readable summary of a file's current visibility.
 *
 * `t` is threaded in rather than obtained from useTranslation() because this is
 * a plain function, not a component — it is called from OCELFileManager during
 * render. It is intentionally REQUIRED (no default): a call site that forgets it
 * should fail loudly instead of silently rendering English.
 *
 * @param {object} file - file entry (file_type, customer_id, allowed_customers)
 * @param {Array<{customer_id:string, display_name:string}>} customers
 * @param {Function} t - i18next t bound to the 'file-manager' namespace
 */
export const accessSummary = (file, customers, t) => {
  const name = (cid) => customers.find((c) => c.customer_id === cid)?.display_name || cid;
  const ov = file.allowed_customers;
  if (ov == null) {
    return file.customer_id
      ? t('access.summaryOnly', { names: name(file.customer_id) })
      : t(file.is_user_upload ? 'access.adminsOnly' : 'access.allUsers');
  }
  if (ov.includes('*')) return t('access.allUsers');
  if (ov.length === 0) return t('access.adminsOnly');
  return t('access.summaryOnly', { names: ov.map(name).join(', ') });
};

/**
 * Inline editor letting an admin set which ETL customers may see a file.
 * Modes: everyone (['*']), specific customers ([ids]), or default (null —
 * revert to the file's origin customer, or public if it has none).
 */
const FileAccessEditor = ({ file, customers, onSaved, onClose }) => {
  const { t } = useTranslation('file-manager');

  const initialMode = useMemo(() => {
    const ov = file.allowed_customers;
    /* eslint-disable i18next/no-literal-string -- 'restricted'/'all'/'admins' are an
       internal mode enum compared at lines below and never rendered; the visible
       labels are separate access.* keys. */
    if (ov == null) return file.customer_id ? 'restricted' : file.is_user_upload ? 'admins' : 'all';
    if (ov.includes('*')) return 'all';
    if (ov.length === 0) return 'admins';
    return 'restricted';
    /* eslint-enable i18next/no-literal-string */
  }, [file.allowed_customers, file.customer_id]);

  const [mode, setMode] = useState(initialMode);
  const [selected, setSelected] = useState(() => {
    const ov = file.allowed_customers;
    if (Array.isArray(ov) && ov.length > 0 && !ov.includes('*')) return new Set(ov);
    // No explicit subset yet — preselect the file's origin customer if it has one
    if (ov == null && file.customer_id) return new Set([file.customer_id]);
    return new Set();
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const toggle = (cid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(cid) ? next.delete(cid) : next.add(cid);
      return next;
    });
  };

  const canSave = mode !== 'restricted' || selected.size > 0;

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      // all users -> ["*"]; admins only -> []; specific customers -> [ids]
      const payload = mode === 'all' ? ['*'] : mode === 'admins' ? [] : [...selected];
      await setFileAccess(file.file_type, payload);
      onSaved?.();
      onClose?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 p-3 border border-indigo-200 bg-indigo-50 rounded-lg">
      <div className="flex items-center gap-2 mb-2 text-sm font-medium text-indigo-900">
        <Shield className="w-4 h-4" />
        {t('access.title')}
      </div>

      <div className="space-y-2 text-sm text-gray-700">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" name={`acc-${file.file_type}`} checked={mode === 'all'}
                 onChange={() => setMode('all')} />
          {t('access.allUsers')}
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" name={`acc-${file.file_type}`} checked={mode === 'restricted'}
                 onChange={() => setMode('restricted')} />
          {t('access.restricted')}
        </label>

        {mode === 'restricted' && (
          <div className="ml-6 mt-1 space-y-1 max-h-40 overflow-y-auto">
            {customers.length === 0 ? (
              <p className="text-xs text-gray-500">{t('access.noCustomers')}</p>
            ) : (
              customers.map((c) => (
                <label key={c.customer_id} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={selected.has(c.customer_id)}
                         onChange={() => toggle(c.customer_id)} />
                  <span>{c.display_name}</span>
                  <span className="text-xs text-gray-400 font-mono">({c.customer_id})</span>
                </label>
              ))
            )}
          </div>
        )}

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" name={`acc-${file.file_type}`} checked={mode === 'admins'}
                 onChange={() => setMode('admins')} />
          {t('access.adminsOnly')}
        </label>
      </div>

      {err && <p className="mt-2 text-xs text-red-600">{err}</p>}

      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={handleSave}
          disabled={saving || !canSave}
          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md transition-colors flex items-center gap-1.5"
        >
          {saving && <Loader className="w-3.5 h-3.5 animate-spin" />}
          {t('access.save')}
        </button>
        <button
          onClick={onClose}
          disabled={saving}
          className="px-3 py-1.5 border border-gray-300 hover:bg-gray-100 text-sm font-medium rounded-md text-gray-700 transition-colors"
        >
          {t('common:actions.cancel')}
        </button>
      </div>
    </div>
  );
};

export default FileAccessEditor;
